import type { ShopperGrowthWeek } from "@/api/types";
import { formatMonthDay, formatPercent } from "@/lib/format";

/**
 * Derivations for the shopper growth dashboard. The API sends the window's
 * weekly signup counts plus one all-time baseline; everything cumulative is
 * computed here so the response never grows with the platform's age.
 */

export interface CumulativeWeek extends ShopperGrowthWeek {
  /** All-time registered shoppers through the end of this week. */
  cumRegistered: number;
  /** All-time shoppers currently opted in, through the end of this week. */
  cumOptedIn: number;
  /** The week still in progress: its counts are to date, not final. */
  partial: boolean;
}

export function withCumulative(
  baseline: { registered: number; optedIn: number },
  weekly: ShopperGrowthWeek[],
  currentWeekStart?: string,
): CumulativeWeek[] {
  let cumRegistered = baseline.registered;
  let cumOptedIn = baseline.optedIn;
  return weekly.map((week) => {
    cumRegistered += week.registered;
    cumOptedIn += week.optedIn;
    return {
      ...week,
      cumRegistered,
      cumOptedIn,
      partial: week.weekStart === currentWeekStart,
    };
  });
}

/** Share of `registered` currently opted in; null when nothing is registered. */
export function optInRate(optedIn: number, registered: number): number | null {
  return registered > 0 ? optedIn / registered : null;
}

/** optInRate as display text; an em dash when nothing is registered. */
export function formatOptInRate(optedIn: number, registered: number): string {
  const rate = optInRate(optedIn, registered);
  return rate == null ? "—" : formatPercent(rate);
}

/**
 * Nearest 1/2/5 x 10^k step (d3's tickStep). Rounding to the nearest step
 * rather than up is what lets a zoomed axis keep a tight floor instead of
 * snapping back to zero.
 */
function tickStep(range: number, count: number): number {
  const raw = range / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  const factor =
    fraction >= 7.07 ? 10 : fraction >= 3.16 ? 5 : fraction >= 1.41 ? 2 : 1;
  return factor * power;
}

/**
 * Axis bounds and ticks zoomed around [lo, hi] with a little headroom, so a
 * running total's slope is readable instead of a flat line over a tall
 * zero-based axis. The floor never drops below zero, and the bottom tick is
 * labeled so a reader can see the axis is truncated. At most nine ticks, on
 * whole numbers, since the axis counts shoppers.
 */
export function zoomedDomain(
  lo: number,
  hi: number,
): { min: number; max: number; ticks: number[] } {
  if (hi <= 0) return { min: 0, max: 1, ticks: [0, 1] };
  const span = Math.max(1, hi - lo);
  const rawLo = Math.max(0, lo - span * 0.08);
  const rawHi = hi + span * 0.05;
  let step = Math.max(1, tickStep(rawHi - rawLo, 5));
  let min = Math.floor(rawLo / step) * step;
  let max = Math.ceil(rawHi / step) * step;
  while ((max - min) / step > 8) {
    step *= 2;
    min = Math.floor(min / step) * step;
    max = Math.ceil(max / step) * step;
  }
  const ticks: number[] = [];
  for (let v = min; v <= max; v += step) ticks.push(v);
  return { min, max, ticks };
}

/**
 * "Week of Apr 6 – Apr 12, 2026" from a Monday ISO date; a week that straddles
 * New Year carries both years ("Week of Dec 29, 2025 – Jan 4, 2026").
 */
export function formatWeekRange(isoDate: string): string {
  const end = new Date(`${isoDate}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  const endIso = end.toISOString().slice(0, 10);
  const startYear = isoDate.slice(0, 4);
  const endYear = endIso.slice(0, 4);
  const start =
    startYear === endYear
      ? formatMonthDay(isoDate)
      : `${formatMonthDay(isoDate)}, ${startYear}`;
  return `Week of ${start} – ${formatMonthDay(endIso)}, ${endYear}`;
}
